import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { flattenWaypoints, poseAt } from '@/lib/path';
import { useSelectionStore } from '@/state/selection';
import { hero, stations, useTourStore } from '@/state/tour';

export function CameraRig() {
  const camera = useThree((s) => s.camera);
  const scene = useThree((s) => s.scene);
  const path = useMemo(() => flattenWaypoints(stations), []);
  const target = useRef({ pos: new THREE.Vector3(), look: new THREE.Vector3() });
  const current = useRef({
    pos: new THREE.Vector3().copy(camera.position),
    look: new THREE.Vector3(0, 4, 0),
  });
  const initialised = useRef(false);

  useEffect(() => {
    if (!useTourStore.getState().dev) return;
    window.__camera = camera;
    window.__scene = scene;
  }, [camera, scene]);

  useFrame((_, dt) => {
    const { progress, reducedMotion } = useTourStore.getState();
    const pose = poseAt(path, progress);
    // Waypoints are authored for the default hero; a bigger one scales the whole path.
    const k = hero.cameraScale;
    target.current.pos.set(...pose.position).multiplyScalar(k);
    target.current.look.set(...pose.lookAt).multiplyScalar(k);
    const focus = useSelectionStore.getState().focus;
    if (focus) {
      // keep the tour's viewing direction, move in to frame the focused elements
      const centre = new THREE.Vector3(...focus.center);
      const dir = target.current.pos.clone().sub(target.current.look).normalize();
      if (dir.y < 0.25) dir.y = 0.25;
      dir.normalize();
      target.current.pos.copy(centre).addScaledVector(dir, Math.max(4, focus.radius * 2.6));
      target.current.look.copy(centre);
    }

    if (!initialised.current || reducedMotion) {
      current.current.pos.copy(target.current.pos);
      current.current.look.copy(target.current.look);
      initialised.current = true;
    } else {
      // frame-rate independent smoothing: k per second
      const k = 1 - Math.exp(-dt * 4.5);
      current.current.pos.lerp(target.current.pos, k);
      current.current.look.lerp(target.current.look, k);
    }
    camera.position.copy(current.current.pos);
    camera.lookAt(current.current.look);
  });

  return null;
}
